"use client"
import React, { useLayoutEffect, useRef, useState } from 'react'
import ProductTag from './product-tag';

const ProductTagRenderer = ({ tags, iconOnly, iconSize }: { tags: any, iconOnly?: boolean, iconSize?: number }) => {
    const filteredTags = iconOnly
        ? tags.filter((tag: any) => tag.show && (tag.iconUrl || tag.iconName))
        : tags.filter((tag: any) => tag.show);

    // Icon-only: compute how many fit, then show +N for leftover
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [visibleCount, setVisibleCount] = useState(filteredTags.length);
    const [overflowCount, setOverflowCount] = useState(0);

    const measure = () => {
        const container = containerRef.current;
        if (!container) return;

        const items = Array.from(container.querySelectorAll<HTMLElement>('[data-tag-item="1"]'));
        if (!items.length) {
            setVisibleCount(0);
            setOverflowCount(0);
            return;
        }

        const style = getComputedStyle(container);
        const gap = parseFloat(style.gap || style.columnGap || "0") || 0;
        const maxWidth = container.clientWidth;

        const widths = items.map(el => el.offsetWidth);
        let used = 0;
        let count = 0;

        for (let i = 0; i < widths.length; i++) {
            const w = widths[i];
            const extra = count > 0 ? gap : 0;
            if (used + extra + w <= maxWidth) {
                used += extra + w;
                count++;
            } else {
                break;
            }
        }

        let leftover = widths.length - count;
        if (leftover > 0) {
            // Measure a worst-case indicator width (e.g. "+99")
            const indicator = document.createElement('span');
            indicator.className = 'inline-flex items-center justify-center rounded-full text-xs px-2 py-1 bg-gray-100 text-gray-700';
            indicator.style.visibility = 'hidden';
            indicator.textContent = '+99';
            container.appendChild(indicator);
            const indicatorW = indicator.offsetWidth;
            container.removeChild(indicator);

            // Ensure "+N" fits; if not, remove last items until it does
            while (count > 0 && (used + (count > 0 ? gap : 0) + indicatorW) > maxWidth) {
                const lastW = widths[count - 1];
                used -= lastW + (count > 1 ? gap : 0);
                count--;
            }
            leftover = widths.length - count;
        }

        setVisibleCount(count);
        setOverflowCount(Math.max(leftover, 0));
    };

    // Recompute on mount, resize, and tag changes
    useLayoutEffect(() => {
        if (!iconOnly) return;
        measure();
        const ro = new ResizeObserver(() => measure());
        if (containerRef.current) ro.observe(containerRef.current);
        return () => ro.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredTags]);

    if (filteredTags.length === 0) return null;

    // When not icon-only, keep original behavior
    if (!iconOnly) {
        return (
            <div className="flex gap-2 items-center flex-wrap">
                {filteredTags.map((tag: any) => (
                    <ProductTag key={tag.id} tag={tag} iconOnly={iconOnly} iconSize={iconSize} />
                ))}
            </div>
        );
    }

    return (
        <div ref={containerRef} className="flex gap-2 items-center flex-nowrap overflow-hidden">
            {filteredTags.slice(0, visibleCount).map((tag: any) => (
                <span key={tag.id} data-tag-item="1" className="inline-flex shrink-0">
                    <ProductTag tag={tag} iconOnly iconSize={iconSize} />
                </span>
            ))}
            {overflowCount > 0 && (
                <span
                    aria-label={`+${overflowCount} more`}
                    className="inline-flex shrink-0 items-center justify-center rounded-full text-xs px-2 py-1 bg-gray-100 text-gray-700"
                >
                    +{overflowCount}
                </span>
            )}
            {/* Ghosts for measurement */}
            {filteredTags.slice(visibleCount).map((tag: any) => (
                <span
                    key={`m-${tag.id}`}
                    data-tag-item="1"
                    className="inline-flex invisible absolute pointer-events-none"
                >
                    <ProductTag tag={tag} iconOnly iconSize={iconSize} />
                </span>
            ))}
        </div>
    );
}

export default ProductTagRenderer